<script lang="ts">
	import { browser } from '$app/env';
	import { page } from '$app/state';
	import { getErrorMessage, pb } from '@/utils/ApiClient';
	import type { ClientResponseError, RecordAuthResponse } from 'pocketbase';
	import { Collections } from '@/pocketbase/pocketbase-types';
	import { Button } from '$lib/components/ui/button/index.js';
	import { Input } from '$lib/components/ui/input/index.js';
	import { Label } from '$lib/components/ui/label/index.js';
	import CircleAlert from '@lucide/svelte/icons/circle-alert';
	import * as Alert from '$lib/components/ui/alert/index.js';
	import { ShipWheel } from '@lucide/svelte';
	import * as m from '$lib/paraglide/messages.js';
	import { localizeHref } from '@/paraglide/runtime';

	let email: string = $state(browser ? (page.url.searchParams.get('email') ?? '') : '');
	let loading: boolean = $state(false);
	let errorToastVisible: boolean = $state(false);
	let toastMessage: string = $state('');
	let showError: boolean = $state(false);
	let errorTitle: string = $state('');
	let emailSent: boolean = $state(false);

	function sendRecoveryEmail() {
		console.info('Sending recovery email');

		loading = true;

		pb.collection(Collections.Users)
			.requestPasswordReset(email)
			.then(() => {
				emailSent = true;
			})
			.catch((error: ClientResponseError) => {
				console.error(error);
				showErrorToast(getErrorMessage(error));
			})
			.finally(() => {
				loading = false;
			});
	}

	function showErrorToast(message: string) {
		toastMessage = message;
		errorToastVisible = true;
		setTimeout(function () {
			errorToastVisible = false;
		}, 4000);
	}
</script>

<svelte:head>
	<title>Recuperar Contraseña - Zarpar | Restablecer Acceso a tu Cuenta</title>
	<meta
		name="description"
		content="Recupera el acceso a tu cuenta de Zarpar. Introduce tu email para recibir un enlace de recuperación de contraseña y volver a acceder a tus reservas náuticas."
	/>
</svelte:head>

<div class="w-full lg:grid lg:min-h-[700px]">
	{#if !emailSent}
		<div class="-mt-20 flex items-center justify-center">
			<div class="mx-auto grid w-[350px] gap-6">
				<div class="grid gap-2 text-center">
					<h1 class="text-3xl font-bold">Recuperar contraseña</h1>
					<p class="text-muted-foreground">
						Introduce tu email para recibir un enlace de recuperación
					</p>
				</div>

				<form
					onsubmit={(e) => {
						e.preventDefault();
						sendRecoveryEmail();
					}}
					class="grid gap-4"
				>
					<div class="grid gap-2">
						<Label for="email">{m.mean_broad_rooster_peek()}</Label>
						<Input
							bind:value={email}
							id="email"
							type="email"
							placeholder="hola@ejemplo.com"
							required
							class="text-base md:text-sm"
						/>
					</div>

					{#if !loading}
						<Button type="submit" class="w-full">Enviar email</Button>
					{:else}
						<Button disabled type="submit" class="w-full"
							><ShipWheel class="mr-4 animate-spin" />{m.patient_knotty_horse_tap()}</Button
						>
					{/if}
				</form>
				{#if showError}
					<Alert.Root variant="destructive">
						<CircleAlert class="h-4 w-4" />
						<Alert.Title>{errorTitle}</Alert.Title>
						<Alert.Description>{toastMessage}</Alert.Description>
					</Alert.Root>
				{/if}
				<div class="mt-4 text-center text-sm">
					{m.ideal_left_hyena_earn()}
					<a href={localizeHref('/registro')} class="underline"> {m.fair_away_moth_gaze()} </a>
				</div>
			</div>
		</div>
	{:else}
		<div class="w-xl mx-auto mt-12 px-4 md:px-0">
			<div class="  z-50 rounded-md bg-green-50 p-4 sm:mt-4">
				<div class="flex justify-center">
					<div class="shrink-0">
						<svg
							class="h-5 w-5 text-green-400"
							viewBox="0 0 20 20"
							fill="currentColor"
							aria-hidden="true"
						>
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
								clip-rule="evenodd"
							></path>
						</svg>
					</div>
					<div class="z-10 ml-3">
						<h3 class="text-sm font-medium text-green-800">Email de recuperación enviado</h3>
						<div class="z-10 mt-2 text-sm text-green-700">
							<p>
								Si email o contraseña existe en nuestro sistema, recibirás un email con un enlace
								para recuperar tu contraseña.
							</p>
						</div>
					</div>
				</div>
			</div>
			<img
				class="mx-auto w-full justify-center sm:max-w-xl"
				src="/img/112766-celebrate.gif"
				alt="welcome animation banner"
			/>
		</div>
	{/if}
</div>
